import {
  Button,
  Dialog,
  Flex,
  Icon,
  ICON_SIZES,
  IconName,
  IndicatorDotVariants,
  IndicatorDotWrapper,
  MixpanelEvent,
  useDisclosure,
  useMixpanel,
  usePopoverState,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { useDisplaySettings } from '../../providers/DisplaySettingsProvider';
import { useShowcaseContext } from '../../providers/ShowcaseProvider';
import type { ShowcaseName } from '../../tokens/showcase';
import { envConfig } from '../../utils/envConfig';
import { currVersion } from '../../utils/version';
import { SidebarItem } from './SidebarItem';
import { PopoverMenu, PopoverMenuItem } from './styles';

export function SidebarReleaseNotesItem() {
  const { latestReleaseNotesRead, setLatestReleaseNotesRead } = useDisplaySettings();
  const mixpanel = useMixpanel();
  const popover = usePopoverState({ trigger: 'hover', placement: 'right', usePortal: true });
  const showWhatsNewDot = latestReleaseNotesRead !== currVersion;
  const { openShowcase, getAvailableShowcases } = useShowcaseContext();
  const showcasePickerDisclosure = useDisclosure();

  const handleReleaseNotesClick = useCallback(() => {
    popover.close();
    mixpanel.track(MixpanelEvent.NavigateToWhatsNew);
    setLatestReleaseNotesRead(currVersion);
  }, [setLatestReleaseNotesRead, mixpanel, popover]);

  const handleWhatsNewClick = useCallback(() => {
    popover.close();
    const available = getAvailableShowcases();
    if (available.length === 0) {
      return;
    }
    if (available.length === 1) {
      openShowcase(available[0].name, true);
    } else {
      showcasePickerDisclosure.open();
    }
  }, [getAvailableShowcases, openShowcase, popover, showcasePickerDisclosure]);

  const handlePickShowcase = useCallback(
    (name: ShowcaseName) => {
      showcasePickerDisclosure.close();
      openShowcase(name, true);
      mixpanel.track(MixpanelEvent.OpenFeatureShowcase, { Title: name });
    },
    [openShowcase, showcasePickerDisclosure, mixpanel]
  );

  const availableShowcases = useMemo(() => getAvailableShowcases(), [getAvailableShowcases]);

  return (
    <>
      <SidebarItem
        data-testid="sidebar-release-notes-item"
        menuItems={
          <PopoverMenu>
            <PopoverMenuItem
              as="a"
              href={envConfig.VITE_RELEASE_NOTES_URL}
              target="_blank"
              onClick={handleReleaseNotesClick}
            >
              <IndicatorDotWrapper show={showWhatsNewDot} variant={IndicatorDotVariants.Notification}>
                <Icon icon={IconName.Newspaper} />
              </IndicatorDotWrapper>
              Release Notes
            </PopoverMenuItem>
            {availableShowcases.length > 0 && (
              <PopoverMenuItem onClick={handleWhatsNewClick}>
                <Icon icon={IconName.PresentationChartLine} />
                What&apos;s New
              </PopoverMenuItem>
            )}
          </PopoverMenu>
        }
      >
        <IndicatorDotWrapper show={showWhatsNewDot} variant={IndicatorDotVariants.Notification}>
          <Icon icon={IconName.Newspaper} size={ICON_SIZES.MEDIUM} />
        </IndicatorDotWrapper>
      </SidebarItem>
      <Dialog
        data-testid="showcase-picker-dialog"
        width={360}
        title="What's New"
        showClose={true}
        suppressFooter={true}
        {...showcasePickerDisclosure}
      >
        <Flex flexDirection="column" gap="spacing8">
          {availableShowcases.map(({ name, title }) => (
            <Button
              data-testid={`showcase-picker-option-${name}`}
              justifyContent="flex-start"
              startIcon={IconName.PresentationChartLine}
              key={name}
              onClick={() => handlePickShowcase(name)}
              flex={1}
            >
              {title}
            </Button>
          ))}
        </Flex>
      </Dialog>
    </>
  );
}
